import React from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@chakra-ui/react';
import { ApplicationState } from '../../../../../store';
import { CustomHeading } from 'shared-components';
import { CustomCollapse } from './../../../../CustomCollapse/CustomCollapse';
import Field from '../../../../Field/Field';
import { PrevNextButtons } from './../../../../PrevNextButtons';
import { FileList } from '../../../../FileList/FileList';
import { useNavigate } from 'react-router-dom';
import ApplicantHeader from '../../../../../components/molecules/applicantHeader';
import { SectionHeader, SectionTitles } from '@/components/molecules/SectionHeader';
import { formatYearMonthDate } from '@/utilities/dateHelpers';
import { getApplicationData } from '@/components/pages/applicant-dashboard/sections/helpers/getApplicationData';
import { combineUnevenArrays } from '@/components/pages/applicant-dashboard/sections/helpers/combineUnevenArrays';
import { useTaskNavigation } from '@/hooks/useTaskNavigation';
import { IEmploymentHistory } from 'types';
import { formatAddress } from '../helpers';
import { useTaskRouteGuard } from '@/hooks/useTaskRouteGuard';
import { TaskLoadingSkeleton } from '@/components/molecules/TaskLoadingSkeleton';
import { useTranslation } from "react-i18next";

export default function Employment(): React.JSX.Element {
    const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { nextRoute, previousRoute } = useTaskNavigation();
  const { application, allDocuments } = useSelector((state: ApplicationState) => state.apply);

  // Route guard - redirects if task is disabled
  const { isLoading } = useTaskRouteGuard('/employment');

  const { initial, current } = getApplicationData(application);

  if (isLoading) {
    return <TaskLoadingSkeleton />;
  }

  const { employmentHistory } = current;
  const { employmentHistory: initialEmploymentHistory } = initial;

  const combinedEmploymentHistory = combineUnevenArrays<IEmploymentHistory>(
    employmentHistory,
    initialEmploymentHistory,
  );

  const showEmploymentHistory = combinedEmploymentHistory.length > 0;

  const files = allDocuments?.cv || [];

  return (
    <>
      <ApplicantHeader pageName="Application" />
      <Box>
        <SectionHeader title={SectionTitles.EMPLOYMENT} />

        <FileList files={files} />

        <Box>
          {!showEmploymentHistory ? (
            <Box bgColor="utility.100" px={5} py={3} mt={3} color="muted">
              {t('common:theApplicantHasNoDataForThisTask')}
                                      </Box>
          ) : (
            <>
              <CustomHeading level="h2" mb={2} mt={6} fontSize="md">
                {t('common:data')}
                                                </CustomHeading>
              {combinedEmploymentHistory.map((data, index) => {
                const startDate = formatYearMonthDate(data.current?.startDate);
                const endDate = data.current?.endDate ? formatYearMonthDate(data.current?.endDate) : 'Present';

                const initialStartDate = formatYearMonthDate(data.original?.startDate);
                const initialEndDate = data.current?.endDate ? formatYearMonthDate(data.original?.endDate) : 'Present';

                const address = formatAddress({
                  addressLine1: data.current?.employerAddress,
                  addressLine2: data.current?.employerAddressLine2,
                  addressLine3: data.current?.employerAddressLine3,
                  city: data.current?.city,
                  postcode: data.current?.postalCode,
                  country: data.current?.country,
                });

                const initialAddress = formatAddress({
                  addressLine1: data.original?.employerAddress,
                  addressLine2: data.original?.employerAddressLine2,
                  addressLine3: data.original?.employerAddressLine3,
                  city: data.original?.city,
                  postcode: data.original?.postalCode,
                  country: data.original?.country,
                });

                return (
                  <CustomCollapse key={`${data.current?.jobTitle ?? data.original?.jobTitle}-${index}`} defaultIsOpen>
                    <CustomCollapse.Trigger>{`Employment from ${startDate} to ${endDate}`}</CustomCollapse.Trigger>
                    <CustomCollapse.Content>
                      <Field
                        leftLabel="Job title"
                        value={data.current?.jobTitle}
                        originalValue={data.original?.jobTitle}
                      />
                      <Field
                        leftLabel="Employer"
                        value={data.current?.employerName}
                        originalValue={data.original?.employerName}
                      />
                      <Field leftLabel="Start date" value={startDate} originalValue={initialStartDate} />
                      <Field leftLabel="End date" value={endDate} originalValue={initialEndDate} />
                      <Field leftLabel="Type" value={data.current?.type} originalValue={data.original?.type} />
                      <Field leftLabel="Address" value={address} originalValue={initialAddress} />
                    </CustomCollapse.Content>
                  </CustomCollapse>
                );
              })}
            </>
          )}
        </Box>

        <Box mt={6}>
          <PrevNextButtons
            onPrevious={previousRoute ? () => navigate(previousRoute) : undefined}
            onNext={nextRoute ? () => navigate(nextRoute) : undefined}
          />
        </Box>
      </Box>
    </>
  );
}
